/**
 * Safe link utilities for tour/artist/venue links.
 * Thin wrappers around src/lib/entityLinks.ts — kept for backwards
 * compatibility with existing call sites. New code should import from
 * `@/lib/entityLinks` directly.
 */

import { artistUrl, tourUrl, venueUrl, isUUID, UUID_REGEX } from "./entityLinks";

// Ticketmaster wrapped tour ID pattern: tm-{alphanumeric}-tour
const TICKETMASTER_TOUR_ID_REGEX = /^tm-[A-Za-z0-9]{6,64}-tour$/;
// Raw Ticketmaster event ID pattern (alphanumeric, typically 10-25 chars)
const TICKETMASTER_RAW_ID_REGEX = /^[A-Za-z0-9]{10,25}$/;

export const isValidUUID = (id: string | null | undefined): boolean => {
  if (!id || typeof id !== "string") return false;
  if (id === "null" || id === "undefined") return false;
  if (id.includes(".")) return false;
  return UUID_REGEX.test(id);
};

export const isValidTicketmasterId = (id: string | null | undefined): boolean => {
  if (!id || typeof id !== "string") return false;
  if (id === "null" || id === "undefined") return false;
  return TICKETMASTER_TOUR_ID_REGEX.test(id) || TICKETMASTER_RAW_ID_REGEX.test(id);
};

export const isValidTourId = (id: string | null | undefined): boolean => {
  return isValidUUID(id) || isValidTicketmasterId(id);
};

/** @deprecated Prefer entityLinks.toSlug. Kept for callers expecting URI-encoded output. */
export const toSlug = (name: string): string => {
  return encodeURIComponent(name.toLowerCase().replace(/\s+/g, "-"));
};

/**
 * Get a tour link. Prefers slug; falls back to UUID/TM id; finally to a search query.
 */
export const getTourLink = (
  tourId: string | null | undefined,
  tourName?: string,
  slug?: string | null,
): string => {
  if (slug) return tourUrl({ slug });
  if (isValidUUID(tourId)) return tourUrl({ id: tourId, name: tourName });
  if (isValidTicketmasterId(tourId)) return `/tours/${tourId}`;
  if (tourName) return `/search?q=${encodeURIComponent(tourName)}&tab=tours`;
  return "/search?tab=tours";
};

export const hasTourLink = (tourId: string | null | undefined, tourName?: string): boolean => {
  return isValidTourId(tourId) || Boolean(tourName?.trim());
};

/**
 * Get an artist link. Prefers slug; falls back to UUID; finally search query.
 */
export const getArtistLink = (
  artistId: string | null | undefined,
  artistName?: string,
  slug?: string | null,
): string => {
  if (slug) return artistUrl({ slug });
  if (isValidUUID(artistId)) return artistUrl({ id: artistId, name: artistName });
  if (artistName) return `/search?q=${encodeURIComponent(artistName)}&tab=artists`;
  return "/search?tab=artists";
};

/**
 * Get a venue link from a "City, Country"-style location string.
 * Slug-only — no query-string fallback.
 */
export const getVenueLink = (location: string | null | undefined): string => {
  if (!location || typeof location !== "string") return "/search?tab=venues";
  return venueUrl({ name: location });
};

export const hasArtistLink = (artistId: string | null | undefined, artistName?: string): boolean => {
  return isValidUUID(artistId) || Boolean(artistName?.trim());
};

/**
 * @deprecated Use getTicketUrl().
 */
export const getTicketmasterUrl = (externalId: string): string => {
  return `https://www.ticketmaster.com/event/${externalId}`;
};

export const getTicketmasterSearchUrl = (artistName: string, city?: string): string => {
  const query = city ? `${artistName} ${city}` : artistName;
  return `https://www.ticketmaster.com/search?q=${encodeURIComponent(query)}`;
};

export const getTicketUrl = (
  ticketUrl: string | null | undefined,
  artistName: string,
  city?: string,
): string => {
  if (ticketUrl) return ticketUrl;
  return getTicketmasterSearchUrl(artistName, city);
};

export const isUpcomingConcert = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;
  // Parse date-only strings as LOCAL dates to avoid UTC off-by-one shifting,
  // which can otherwise misclassify a same-day/next-day show as "past".
  let concertDate: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [y, m, d] = dateString.split("-").map(Number);
    concertDate = new Date(y, m - 1, d);
  } else {
    concertDate = new Date(dateString);
  }
  concertDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return concertDate >= today;
};
